import { apiFetch } from '@/lib/api/http';

/** 外部連携（OAuth状態機械）（指示書 §6・§11・§12 M11 / DECISIONS D-043・D-044）. */
export type IntegrationProvider = 'google' | 'slack' | 'chatwork' | 'smtp';

export const PROVIDER_LABELS: Record<IntegrationProvider, string> = {
  google: 'Google（ログイン連携）',
  slack: 'Slack（Incoming Webhook）',
  chatwork: 'Chatwork（APIトークン）',
  smtp: 'メール（SMTP）',
};

export type OAuthStatus =
  | 'not_connected'
  | 'authorizing'
  | 'pending_approval'
  | 'connected'
  | 'sync_succeeded'
  | 'expired'
  | 'failed';

export const OAUTH_STATUS_LABELS: Record<OAuthStatus, string> = {
  not_connected: '未連携',
  authorizing: '認証中',
  pending_approval: '承認待ち',
  connected: '連携済み',
  sync_succeeded: '同期成功',
  expired: '期限切れ',
  failed: '失敗',
};

export interface Integration {
  id: string;
  provider: IntegrationProvider;
  oauth_status: OAuthStatus;
  is_enabled: boolean;
  last_sync_at: string | null;
  last_error: string | null;
}

export function fetchIntegrations(): Promise<Integration[]> {
  return apiFetch<Integration[]>('/integrations');
}

export function connectIntegration(integrationId: string, token?: string): Promise<Integration> {
  return apiFetch<Integration>(`/integrations/${integrationId}/connect`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: token || null }),
  });
}

export function disconnectIntegration(integrationId: string): Promise<Integration> {
  return apiFetch<Integration>(`/integrations/${integrationId}/disconnect`, { method: 'POST' });
}

export function syncIntegration(integrationId: string, succeed: boolean): Promise<Integration> {
  return apiFetch<Integration>(`/integrations/${integrationId}/sync`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ succeed }),
  });
}

// --- 接続設定（事務局が管理画面から設定する / D-067） ---------------------------

export type SettingFieldType = 'text' | 'secret' | 'bool' | 'number';

export interface IntegrationSettingField {
  key: string;
  label: string;
  type: SettingFieldType;
  help: string;
  /** app=管理画面で設定済み / env=サーバーの環境変数を使用中 */
  source: 'app' | 'env';
  /** secret は常に null（値は返らない） */
  value: string | number | boolean | null;
  is_set: boolean;
}

export interface IntegrationSettings {
  integration_id: string;
  provider: IntegrationProvider;
  fields: IntegrationSettingField[];
}

export function fetchIntegrationSettings(integrationId: string): Promise<IntegrationSettings> {
  return apiFetch<IntegrationSettings>(`/integrations/${integrationId}/settings`);
}

export function updateIntegrationSettings(
  integrationId: string,
  values: Record<string, string | number | boolean | null>,
): Promise<IntegrationSettings> {
  return apiFetch<IntegrationSettings>(`/integrations/${integrationId}/settings`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ values }),
  });
}
